import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Briefcase, ArrowLeft, Video, MessageSquare, Users, Clock, Play, Square, UserPlus, Send, Zap, Radio } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HiringEvents() {
  const { user } = useAuth();
  const { eventId } = useParams();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', event_type: 'chat', meeting_link: '' });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (eventId) {
      loadEvent(eventId);
    }
  }, [eventId]);

  // Poll event for updates when viewing
  useEffect(() => {
    if (selectedEvent) {
      pollRef.current = setInterval(() => loadEvent(selectedEvent.id), 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [selectedEvent?.id]);

  const fetchEvents = async () => {
    try {
      const { data } = await axios.get(`${API}/hiring-events`);
      setEvents(data.events);
    } catch (err) { console.error(err); }
  };

  const loadEvent = async (id) => {
    try {
      const { data } = await axios.get(`${API}/hiring-events/${id}`);
      setSelectedEvent(data);
    } catch (err) { console.error(err); }
  };

  const createEvent = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/hiring-events`, form, { withCredentials: true });
      setShowCreate(false);
      fetchEvents();
      loadEvent(data.event_id);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create event'); }
    setLoading(false);
  };

  const joinEvent = async () => {
    try {
      const { data } = await axios.post(`${API}/hiring-events/${selectedEvent.id}/join`, {}, { withCredentials: true });
      alert(`${data.message}. Position: #${data.position}`);
      loadEvent(selectedEvent.id);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to join'); }
  };

  const startEvent = async () => {
    try {
      await axios.post(`${API}/hiring-events/${selectedEvent.id}/start`, {}, { withCredentials: true });
      loadEvent(selectedEvent.id);
    } catch (err) { console.error(err); }
  };

  const endEvent = async () => {
    try {
      await axios.post(`${API}/hiring-events/${selectedEvent.id}/end`, {}, { withCredentials: true });
      loadEvent(selectedEvent.id);
    } catch (err) { console.error(err); }
  };

  const nextCandidate = async () => {
    try {
      const { data } = await axios.post(`${API}/hiring-events/${selectedEvent.id}/next-candidate`, {}, { withCredentials: true });
      if (data.candidate) {
        loadEvent(selectedEvent.id);
      } else {
        alert('No more candidates waiting');
      }
    } catch (err) { console.error(err); }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    try {
      await axios.post(`${API}/hiring-events/${selectedEvent.id}/message`, { content: message }, { withCredentials: true });
      setMessage('');
      loadEvent(selectedEvent.id);
    } catch (err) { console.error(err); }
  };

  const isHost = selectedEvent && user && selectedEvent.host_id === user._id;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>Live Hiring Events</h1>
            <p className="text-[#1C2A24]/60 mt-1">Join live hiring sessions or create your own</p>
          </div>
          {(user?.role === 'business' || user?.role === 'admin') && (
            <Button onClick={() => setShowCreate(true)} className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white" data-testid="create-event-btn">
              <Video size={16} className="mr-2" /> Create Hiring Event
            </Button>
          )}
        </div>

        {/* Create Event Form */}
        {showCreate && (
          <form onSubmit={createEvent} className="bg-white rounded-3xl border border-[#E5E0D8] p-8 mb-6 space-y-4" data-testid="create-event-form">
            <h2 className="text-xl font-medium" style={{fontFamily:'Outfit'}}>Create Hiring Event</h2>
            <div>
              <Label>Event Title</Label>
              <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" placeholder="e.g. Customer Service Hiring Day" required data-testid="event-title" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={3} required data-testid="event-desc" />
            </div>
            <div>
              <Label>Interview Type</Label>
              <Select value={form.event_type} onValueChange={v => setForm(f => ({...f, event_type: v}))}>
                <SelectTrigger className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" data-testid="event-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="chat"><div className="flex items-center gap-2"><MessageSquare size={14} /> Text Chat Interview</div></SelectItem>
                  <SelectItem value="video"><div className="flex items-center gap-2"><Video size={14} /> Video Interview (Zoom/Meet)</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.event_type === 'video' && (
              <div>
                <Label>Meeting Link (Zoom, Google Meet, etc.)</Label>
                <Input value={form.meeting_link} onChange={e => setForm(f => ({...f, meeting_link: e.target.value}))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" placeholder="https://zoom.us/j/... or https://meet.google.com/..." data-testid="meeting-link-input" />
                <p className="text-xs text-[#1C2A24]/40 mt-1">Paste your Zoom or Google Meet link. Candidates will see this when they're admitted to interview.</p>
              </div>
            )}
            <div className="flex gap-3">
              <Button type="submit" disabled={loading} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="submit-event-btn">
                {loading ? 'Creating...' : 'Create Event'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)} className="rounded-full">Cancel</Button>
            </div>
          </form>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          {/* Event List */}
          <div className="md:col-span-1 space-y-3" data-testid="events-list">
            {events.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E5E0D8] p-8 text-center">
                <Radio size={32} className="text-[#E5E0D8] mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-[#1C2A24]/40 text-sm">No hiring events yet</p>
              </div>
            ) : events.map(ev => (
              <div key={ev.id} onClick={() => loadEvent(ev.id)}
                className={`bg-white rounded-2xl border p-4 cursor-pointer transition-all ${selectedEvent?.id === ev.id ? 'border-[#047857] shadow-md' : 'border-[#E5E0D8] hover:border-[#047857]/30'}`}
                data-testid={`event-card-${ev.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  {ev.status === 'live' ? (
                    <span className="flex items-center gap-1 text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium"><span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>LIVE</span>
                  ) : ev.status === 'ended' ? (
                    <span className="text-[10px] bg-[#F4F1E8] text-[#1C2A24]/50 px-2 py-0.5 rounded-full">Ended</span>
                  ) : (
                    <span className="text-[10px] bg-[#A7F3D0]/30 text-[#047857] px-2 py-0.5 rounded-full">Scheduled</span>
                  )}
                  <span className="text-[10px] text-[#1C2A24]/40">{ev.event_type === 'video' ? 'Video' : 'Chat'}</span>
                </div>
                <h3 className="font-medium text-sm truncate" style={{fontFamily:'Outfit'}}>{ev.title}</h3>
                <p className="text-xs text-[#1C2A24]/50 mt-1">{ev.company_name || ev.host_name} &middot; {(ev.waiting_room || []).length} waiting</p>
              </div>
            ))}
          </div>

          {/* Event Detail */}
          <div className="md:col-span-2">
            {!selectedEvent ? (
              <div className="bg-white rounded-3xl border border-[#E5E0D8] p-12 text-center">
                <Video size={48} className="text-[#E5E0D8] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[#1C2A24]/50">Select a hiring event to view details</p>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-[#E5E0D8] overflow-hidden" data-testid="event-detail">
                {/* Header */}
                <div className="p-6 border-b border-[#E5E0D8] bg-[#F4F1E8]/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {selectedEvent.status === 'live' && <span className="flex items-center gap-1 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium"><span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>LIVE NOW</span>}
                        <span className="tag-pill text-[10px]">{selectedEvent.event_type === 'video' ? 'Video Interview' : 'Chat Interview'}</span>
                      </div>
                      <h2 className="text-xl font-medium" style={{fontFamily:'Outfit'}}>{selectedEvent.title}</h2>
                      <p className="text-sm text-[#1C2A24]/60 mt-1">{selectedEvent.description}</p>
                    </div>
                    {isHost && (
                      <div className="flex gap-2">
                        {selectedEvent.status === 'scheduled' && (
                          <Button onClick={startEvent} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="start-event-btn">
                            <Play size={14} className="mr-1" /> Go Live
                          </Button>
                        )}
                        {selectedEvent.status === 'live' && (
                          <>
                            <Button onClick={nextCandidate} className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white" data-testid="next-candidate-btn">
                              <UserPlus size={14} className="mr-1" /> Next Candidate
                            </Button>
                            <Button onClick={endEvent} variant="outline" className="rounded-full" data-testid="end-event-btn">
                              <Square size={14} className="mr-1" /> End
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Waiting Room & Active Candidate */}
                <div className="p-6 grid grid-cols-2 gap-4 border-b border-[#E5E0D8]">
                  <div>
                    <h3 className="text-sm font-medium mb-3 flex items-center gap-2" style={{fontFamily:'Outfit'}}><Clock size={14} /> Waiting Room ({(selectedEvent.waiting_room || []).length})</h3>
                    <div className="space-y-2 max-h-[150px] overflow-y-auto">
                      {(selectedEvent.waiting_room || []).map((c, i) => (
                        <div key={c.user_id} className="bg-[#F4F1E8]/50 rounded-xl p-3 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-[#047857]/10 flex items-center justify-center text-xs font-medium text-[#047857]">#{i+1}</span>
                          <div>
                            <p className="text-sm font-medium">{c.name}</p>
                            <p className="text-xs text-[#1C2A24]/40">{(c.skills || []).slice(0, 3).join(', ')}</p>
                          </div>
                        </div>
                      ))}
                      {(selectedEvent.waiting_room || []).length === 0 && <p className="text-xs text-[#1C2A24]/30">No candidates waiting</p>}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium mb-3 flex items-center gap-2" style={{fontFamily:'Outfit'}}><Zap size={14} className="text-[#047857]" /> Active Interview</h3>
                    {selectedEvent.active_candidate ? (
                      <div className="bg-[#A7F3D0]/20 rounded-xl p-4 border border-[#047857]/10">
                        <p className="font-medium text-sm">{selectedEvent.active_candidate.name}</p>
                        <p className="text-xs text-[#1C2A24]/50 mt-1">{selectedEvent.active_candidate.email}</p>
                        <p className="text-xs text-[#047857] mt-1">{(selectedEvent.active_candidate.skills || []).join(', ')}</p>
                        {selectedEvent.meeting_link && selectedEvent.event_type === 'video' && (
                          <a href={selectedEvent.meeting_link} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-xs bg-[#047857] text-white px-3 py-1.5 rounded-full hover:bg-[#065F46] transition-colors" data-testid="join-video-btn">
                            <Video size={12} /> Join Video Call
                          </a>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-[#1C2A24]/30">No active interview</p>
                    )}
                  </div>
                </div>

                {/* Join Button for Candidates */}
                {user && !isHost && selectedEvent.status === 'live' && (
                  <div className="p-4 border-b border-[#E5E0D8] text-center">
                    <Button onClick={joinEvent} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="join-event-btn">
                      <UserPlus size={14} className="mr-2" /> Join Waiting Room
                    </Button>
                  </div>
                )}

                {/* Chat Messages */}
                <div className="p-4 max-h-[250px] overflow-y-auto space-y-3">
                  {(selectedEvent.messages || []).length === 0 ? (
                    <p className="text-center text-[#1C2A24]/30 py-4 text-sm">No messages yet</p>
                  ) : (selectedEvent.messages || []).map(m => (
                    <div key={m.id} className="bg-[#F4F1E8]/50 rounded-xl p-3">
                      <p className="text-xs font-medium text-[#047857]">{m.user_name}</p>
                      <p className="text-sm text-[#1C2A24]/80 mt-1">{m.content}</p>
                    </div>
                  ))}
                </div>

                {/* Chat Input */}
                {user && selectedEvent.status === 'live' && (
                  <div className="p-4 border-t border-[#E5E0D8] flex gap-3">
                    <Input value={message} onChange={e => setMessage(e.target.value)} placeholder="Type a message..."
                      className="rounded-full bg-[#F4F1E8] border-[#E5E0D8]" data-testid="event-chat-input"
                      onKeyDown={e => e.key === 'Enter' && sendMessage()} />
                    <Button onClick={sendMessage} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white shrink-0" data-testid="event-send-btn">
                      <Send size={16} strokeWidth={1.5} />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
